import { useMemo, useState } from 'preact/hooks';
import { ChevronLeft, ChevronRight } from 'lucide-preact';
import dayjs, { type Dayjs } from 'dayjs';
import { cn } from '@/lib/cn';

const WEEK_DAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

export interface CalendarProps {
  value?: Date | null;
  rangeStart?: Date | null;
  rangeEnd?: Date | null;
  onSelect: (date: Date) => void;
  min?: Date;
  max?: Date;
  className?: string;
}

function startOfMonthGrid(d: Dayjs): Dayjs {
  // Lunes como primer día (Spain)
  const first = d.startOf('month');
  const dayOfWeek = (first.day() + 6) % 7; // 0 = Mon
  return first.subtract(dayOfWeek, 'day');
}

export function Calendar({ value, rangeStart, rangeEnd, onSelect, min, max, className }: CalendarProps) {
  const [view, setView] = useState<Dayjs>(() => dayjs(value ?? rangeStart ?? new Date()));

  const days = useMemo(() => {
    const start = startOfMonthGrid(view);
    return Array.from({ length: 42 }, (_, i) => start.add(i, 'day'));
  }, [view]);

  function isInRange(d: Dayjs) {
    if (!rangeStart || !rangeEnd) return false;
    const a = dayjs(rangeStart);
    const b = dayjs(rangeEnd);
    return d.isAfter(a.subtract(1, 'day')) && d.isBefore(b.add(1, 'day'));
  }

  function isDisabled(d: Dayjs) {
    if (min && d.isBefore(dayjs(min), 'day')) return true;
    if (max && d.isAfter(dayjs(max), 'day')) return true;
    return false;
  }

  return (
    <div className={cn('w-[280px] select-none p-2', className)}>
      <div className="flex items-center justify-between px-1 pb-2">
        <button
          type="button"
          aria-label="Mes anterior"
          onClick={() => setView((v) => v.subtract(1, 'month'))}
          className="flex h-7 w-7 items-center justify-center rounded text-fg-muted hover:bg-surface-2 hover:text-fg-strong"
        >
          <ChevronLeft size={14} />
        </button>
        <p className="text-13 font-semibold capitalize text-fg-strong">
          {view.format('MMMM YYYY')}
        </p>
        <button
          type="button"
          aria-label="Mes siguiente"
          onClick={() => setView((v) => v.add(1, 'month'))}
          className="flex h-7 w-7 items-center justify-center rounded text-fg-muted hover:bg-surface-2 hover:text-fg-strong"
        >
          <ChevronRight size={14} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-px text-center">
        {WEEK_DAYS.map((d) => (
          <p key={d} className="py-1 text-3xs font-medium uppercase text-fg-faint">
            {d}
          </p>
        ))}
        {days.map((d) => {
          const inMonth = d.month() === view.month();
          const today = d.isSame(dayjs(), 'day');
          const isStart = rangeStart && d.isSame(rangeStart, 'day');
          const isEnd = rangeEnd && d.isSame(rangeEnd, 'day');
          const inRange = isInRange(d);
          const isSel = (value && d.isSame(value, 'day')) || isStart || isEnd;
          const disabled = isDisabled(d);
          return (
            <button
              key={d.toISOString()}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(d.toDate())}
              className={cn(
                'h-7 w-7 rounded text-xs font-medium transition-colors duration-med cursor-pointer outline-none focus-visible:shadow-focus',
                disabled && 'opacity-30 cursor-not-allowed',
                !inMonth && 'text-fg-faint',
                inMonth && !isSel && !inRange && 'text-fg hover:bg-surface-2',
                inRange && !isSel && 'bg-brand-tint text-brand',
                isSel && 'bg-brand text-brand-contrast hover:bg-brand-hover',
                today && !isSel && 'ring-1 ring-brand/40',
              )}
              aria-label={d.format('DD MMMM YYYY')}
              aria-selected={!!isSel}
            >
              {d.date()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
